import { Grid, makeStyles } from '@material-ui/core'
import Tooltip from 'components/common/Tooltip'
import InfoOutlined from 'components/common/icons/InfoOutlined'
import DazzedHeadingForGrid from 'components/common/text/DazzedHeadingForGrid'
import RightArrowIcon from 'components/CopilotControl/icons/RightArrowIcon'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useCanViewCopilotPatients } from '../../hooks/useCopilotAccountOwnerId'
import BaseWidget from '../../components/common/BaseWidget'
import NumberWidgetSkeleton from '../../components/common/NumberWidget/NumberWidgetSkeleton'

const useStyles = makeStyles(theme => ({
  // No fixed height: BaseWidget's 5px margin must stay inside the column slot so the cards keep
  // Copilot's 10px gap instead of overflowing into each other.
  root: {},
  // Figma frames 217 and 220: label baseline 37px and value baseline 89px below the card top.
  // Let long labels grow the card instead of clipping the value.
  container: {
    position: 'relative',
    minHeight: 124,
    padding: '22.5px 24px 24px',
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    rowGap: 12
  },
  icon: {
    display: 'flex',
    flexShrink: 0,
    alignSelf: 'center',
    justifyContent: 'center',
    width: 52,
    marginRight: 28
  },
  content: {
    minWidth: 0,
    // Keep the designed count and unit together; the action wraps first on narrow cards.
    flex: '1 0 172px'
  },
  label: {
    display: 'flex',
    alignItems: 'center'
  },
  tooltip: {
    marginLeft: 6,
    display: 'flex',
    '& svg': {
      width: 14,
      height: 14
    }
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    marginTop: 14.5,
    flexWrap: 'wrap'
  },
  // The card is too short for Copilot's overlay footer, so the link sits beside the value instead
  // of covering it; the label and its info icon stay reachable while hovering. Visibility follows
  // the card's :hover / :focus-within in CSS so the link is clickable the moment the pointer arrives.
  seePatientsButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    alignSelf: 'center',
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    font: 'inherit',
    transition: 'opacity 0.3s',
    opacity: 0,
    pointerEvents: 'none',
    '$container:hover &, $container:focus-within &': {
      opacity: 1,
      pointerEvents: 'auto'
    }
  },
  seePatientsText: {
    color: '#3C52EF',
    fontFamily: 'Dazzed',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '24px',
    whiteSpace: 'nowrap',
    [theme.breakpoints.up('lg')]: {
      fontSize: 14
    }
  },
  errorState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  }
}))

const OralHygieneNumberCard = ({ label, tooltip, icon, value = 0, isLoading, isError, isSelected, onSeePatients }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  // A Sales-only doctor override shows totals without patient lists, so there is nothing to open.
  const canViewPatients = useCanViewCopilotPatients()
  const showSeePatients = canViewPatients && value > 0 && !isLoading && !isError

  // NumberWidgetSkeleton carries its own 100px box and padding, so it renders in place of the
  // padded container rather than inside it.
  if (isLoading) {
    return (
      <BaseWidget className={classes.root} carousel={false} isSelected={isSelected} removePadding>
        <NumberWidgetSkeleton />
      </BaseWidget>
    )
  }

  return (
    <BaseWidget className={classes.root} carousel={false} isSelected={isSelected} removePadding>
      <div className={classes.container}>
        {isError ? (
          <div className={classes.errorState}>{t('general.genericError')}</div>
        ) : (
          <>
            {icon && <div className={classes.icon}>{icon}</div>}
            <Grid container direction="column" className={classes.content}>
              <Grid item className={classes.label}>
                <DazzedHeadingForGrid regular xs={16} style={{ lineHeight: 1 }}>
                  {label}
                </DazzedHeadingForGrid>
                {tooltip && (
                  <Tooltip value={tooltip} containerClassName={classes.tooltip} placement="top" arrow variant="light">
                    <InfoOutlined color="#BEC6D6" />
                  </Tooltip>
                )}
              </Grid>
              <Grid item className={classes.valueRow}>
                <DazzedHeadingForGrid xs={40} style={{ fontWeight: 600, lineHeight: 1, letterSpacing: '-0.02em' }}>
                  {value}
                </DazzedHeadingForGrid>
                <DazzedHeadingForGrid xs={26} style={{ fontWeight: 600, lineHeight: 1, letterSpacing: '-0.02em' }}>
                  {t('pages.oralHygieneReport.widgets.patients')}
                </DazzedHeadingForGrid>
              </Grid>
            </Grid>
            {showSeePatients && (
              <button type="button" className={classes.seePatientsButton} onClick={onSeePatients}>
                <span className={classes.seePatientsText}>{t('pages.oralHygieneReport.widgets.seePatients')}</span>
                <RightArrowIcon />
              </button>
            )}
          </>
        )}
      </div>
    </BaseWidget>
  )
}

export default OralHygieneNumberCard
